/// <amd-module name="Wit/Common/KanbanBoardSettings/Components/CardReorderingView" />
import * as React from "react";
import { IObservableValue, ObservableValue, useSubscription } from "VSS/Core/Observable";
import { usePageContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Header, TitleSize } from "VSSUI/Header";
import { Image } from "VSSUI/Image";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import * as Resources from "Wit/Common/KanbanBoardSettings/Resources";
import { ISettingsView } from "Wit/Common/WitSettingsPanelTypes/Components/WitSettingsPanel.props";

export function getCardReorderingTab(
    preserveBacklogOrder: boolean,
    disabled: boolean,
    groupId?: string,
    saveSettings?: (preserverBacklogOrder: boolean) => Promise<void>
): ISettingsView {
    const isDirty = new ObservableValue(false);
    const isValid = new ObservableValue(true);
    const reorderSetting = preserveBacklogOrder ? CardReorderingOptions.FollowBacklogOrder : CardReorderingOptions.UpdateBacklogOrder;
    const selectedReorderOption = new ObservableValue(reorderSetting);
    return {
        groupId,
        isDirty: isDirty,
        isValid: isValid,
        name: Resources.CardReorderingTab,
        onSaveSettings: saveSettings ? () => saveSettings(selectedReorderOption.value === CardReorderingOptions.FollowBacklogOrder) : undefined,
        renderContent: () => (
            <CardReorderingView
                disabled={disabled}
                isDirty={isDirty}
                originalReorderOption={reorderSetting}
                selectedReorderOption={selectedReorderOption}
            />
        )
    };
}

enum CardReorderingOptions {
    FollowBacklogOrder = "followBacklogOrder",
    UpdateBacklogOrder = "updateBacklogOrder"
}

interface ICardReorderingViewProps {
    disabled: boolean;
    isDirty: IObservableValue<boolean>;
    originalReorderOption: CardReorderingOptions;
    selectedReorderOption: IObservableValue<string>;
}

const CardReorderingView: React.FunctionComponent<ICardReorderingViewProps> = ({
    disabled,
    isDirty,
    originalReorderOption,
    selectedReorderOption
}) => {
    const pageContext = usePageContext();
    const preserveOrderGif = React.useRef(
        getAssetLocation(pageContext, "ms.vss-work-web/kanban-board-settings/Content/boardpreservebacklogorder.gif")
    );
    const updateOrderGif = React.useRef(
        getAssetLocation(pageContext, "ms.vss-work-web/kanban-board-settings/Content/boardallowbacklogreodering.gif")
    );

    const updateIsDirty = () => {
        const updatedIsDirty = originalReorderOption !== selectedReorderOption.value;
        if (updatedIsDirty !== isDirty.value) {
            isDirty.value = updatedIsDirty;
        }
    };

    return (
        <div className="flex-column rhythm-vertical-24">
            <Header
                className="padding-vertical-0"
                description={Resources.CardReorderingDescription}
                title={Resources.CardReorderingTab}
                titleSize={TitleSize.Small}
            />
            <div className="flex-column rhythm-vertical-24 padding-horizontal-20">
                <RadioButtonGroup
                    className="flex-noshrink"
                    onSelect={buttonId => {
                        selectedReorderOption.value = buttonId;
                        updateIsDirty();
                    }}
                    selectedButtonId={selectedReorderOption}
                >
                    <RadioButton disabled={disabled} id={CardReorderingOptions.UpdateBacklogOrder} text={Resources.UpdateBacklogOrderButton} />
                    <ActivatableGif
                        gifPath={updateOrderGif.current}
                        id={CardReorderingOptions.UpdateBacklogOrder}
                        selectedReorderOption={selectedReorderOption}
                    />
                    <RadioButton disabled={disabled} id={CardReorderingOptions.FollowBacklogOrder} text={Resources.FollowBacklogOrderButton} />
                    <ActivatableGif
                        gifPath={preserveOrderGif.current}
                        id={CardReorderingOptions.FollowBacklogOrder}
                        selectedReorderOption={selectedReorderOption}
                    />
                </RadioButtonGroup>
            </div>
        </div>
    );
};

interface IActivatableGifProps {
    gifPath: string;
    id: string;
    selectedReorderOption: IObservableValue<string>;
}

const ActivatableGif: React.FunctionComponent<IActivatableGifProps> = ({ gifPath, id, selectedReorderOption }) => {
    const pageContext = usePageContext();
    const imagePath = React.useRef(getAssetLocation(pageContext, "ms.vss-work-web/kanban-board-settings/Content/board.png"));
    const [gifActivated, setGifActivated] = React.useState(false);
    useSubscription(
        selectedReorderOption,
        reorderChoice => {
            const isSelected = reorderChoice === id;
            if (isSelected && !gifActivated) {
                setGifActivated(true);
            }
        },
        [id]
    );
    return (
        <div className="cursor-pointer padding-16" onClick={() => setGifActivated(!gifActivated)}>
            <Image role="presentation" height={120} src={gifActivated ? gifPath : imagePath.current} width={200} />
        </div>
    );
};
